import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Re-ping Bullhorn at most every 2 minutes to avoid spamming the endpoint
// even if the user triggers multiple focus/visibility events in quick succession.
const BULLHORN_ENSURE_DEBOUNCE_MS = 2 * 60 * 1000;
// Fire a proactive check every 8 minutes while the tab is active so the
// BhRestToken (which dies after ~10 min of inactivity) stays warm.
const BULLHORN_ENSURE_INTERVAL_MS = 8 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastEnsureAtRef = useRef(0);
  const userRef = useRef(null);

  // Keep a ref to `user` so the focus/interval handlers always see the latest
  // without needing to re-subscribe (which would cause reconnect churn).
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Silent, idempotent Bullhorn reconnection check. Calls the lightweight
  // `/tenants/me/bullhorn/ensure-connection` endpoint which only re-authenticates
  // when the BhRestToken session has actually died. Safe to call as often as
  // every app entry / tab focus / periodic timer — backend short-circuits via
  // a cheap /ping when the session is still alive.
  const ensureBullhornConnection = useCallback(async ({ force = false } = {}) => {
    const currentUser = userRef.current;
    if (!currentUser || currentUser.is_master_admin) return;

    const now = Date.now();
    if (!force && now - lastEnsureAtRef.current < BULLHORN_ENSURE_DEBOUNCE_MS) {
      return;
    }
    lastEnsureAtRef.current = now;

    try {
      await axios.post(`${API_URL}/tenants/me/bullhorn/ensure-connection`);
    } catch {
      // Non-blocking — user can still use the app if Bullhorn is misconfigured
    }
  }, []);

  // On mount, check if we have a valid session via httpOnly cookie
  useEffect(() => {
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proactively keep the Bullhorn session warm:
  //   1. Re-check whenever the tab regains focus / becomes visible
  //      (handles the classic "came back after lunch" scenario).
  //   2. Poll every BULLHORN_ENSURE_INTERVAL_MS while the tab is active so the
  //      BhRestToken never falls into the ~10-minute inactivity window.
  useEffect(() => {
    if (!user || user.is_master_admin) return undefined;

    const onFocus = () => ensureBullhornConnection();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') ensureBullhornConnection();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = setInterval(() => ensureBullhornConnection({ force: true }), BULLHORN_ENSURE_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(intervalId);
    };
  }, [user, ensureBullhornConnection]);

  const checkSession = async () => {
    try {
      const userResponse = await axios.get(`${API_URL}/auth/me`);
      const userData = userResponse.data;
      setUser(userData);
      
      if (userData.is_master_admin) {
        setTenant({ slug: 'master', name: 'Master Admin', id: 'master' });
        localStorage.setItem('tenantSlug', 'master');
        setLoading(false);
        return;
      }
      
      try {
        const tenantResponse = await axios.get(`${API_URL}/tenants/current`);
        const tenantData = tenantResponse.data;
        setTenant(tenantData);
        localStorage.setItem('tenantSlug', tenantData.slug);

        // Proactively ensure the Bullhorn session is live on every app entry
        // (includes Bullhorn Custom Tab iframe loads where there's no
        // interactive login step). Bypass the userRef-based helper here —
        // userRef hasn't been updated by its useEffect yet so the helper
        // would early-return; call the endpoint directly instead.
        try {
          await axios.post(`${API_URL}/tenants/me/bullhorn/ensure-connection`);
          lastEnsureAtRef.current = Date.now();
          // Refresh the tenant once so the dashboard reflects the now-live
          // `bullhorn_config.is_connected` flag without waiting for a nav.
          try {
            const refreshed = await axios.get(`${API_URL}/tenants/current`);
            setTenant(refreshed.data);
          } catch {
            // best-effort refresh
          }
        } catch {
          // Bullhorn auto-connect failure is non-blocking
        }
      } catch (tenantError) {
        console.warn('Failed to fetch tenant:', tenantError);
        setTenant({ slug: 'default', name: 'Default', id: 'default' });
      }
    } catch {
      setUser(null);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { user: userData, tenant: tenantData } = response.data;
    setUser(userData);
    
    if (tenantData) {
      setTenant(tenantData);
      localStorage.setItem('tenantSlug', tenantData.slug);
      
      // Auto-connect to Bullhorn if credentials are configured. We AWAIT the
      // call so the dashboard sees `is_connected: true` on first render
      // (previously this fired-and-forgot, leaving the dashboard in a "not
      // connected" state until the user clicked Settings).
      if (!userData.is_master_admin && tenantData.id) {
        try {
          const connectRes = await axios.post(`${API_URL}/tenants/me/bullhorn/ensure-connection`);
          lastEnsureAtRef.current = Date.now();
          if (connectRes.data?.auto_connected) {
            // Refresh tenant data so `bullhorn_config.is_connected` flips
            try {
              const refreshed = await axios.get(`${API_URL}/tenants/current`);
              setTenant(refreshed.data);
            } catch {
              // tenant refresh failed — keep the original; dashboard will reload on nav
            }
          }
        } catch {
          // Bullhorn auto-connect failure is non-blocking — user can still use the app
        }
      }
    } else {
      const defaultTenant = { slug: 'default', name: 'Default', id: 'default' };
      setTenant(defaultTenant);
      localStorage.setItem('tenantSlug', 'default');
    }
    
    return { user: userData, tenant: tenantData || { slug: 'default', name: 'Default', id: 'default' } };
  };

  const register = async (email, password, name, companyName = null) => {
    const response = await axios.post(`${API_URL}/auth/register`, { 
      email, password, name, company_name: companyName
    });
    const { user: userData, tenant: tenantData } = response.data;
    setUser(userData);
    
    if (tenantData) {
      setTenant(tenantData);
      localStorage.setItem('tenantSlug', tenantData.slug);
      return { user: userData, tenant: tenantData };
    }
    
    const defaultTenant = { slug: 'default', name: 'Default', id: 'default' };
    setTenant(defaultTenant);
    localStorage.setItem('tenantSlug', 'default');
    return { user: userData, tenant: defaultTenant };
  };

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch {
      // Ignore errors — we clear state regardless
    }
    localStorage.removeItem('tenantSlug');
    setUser(null);
    setTenant(null);
  }, []);

  // Get the base path for tenant-scoped routes
  const getTenantPath = (path = '') => {
    const slug = tenant?.slug || localStorage.getItem('tenantSlug') || 'default';
    return `/${slug}${path}`;
  };

  // Re-fetch the current tenant so callers can update `settings`
  // (e.g. after a feature-flag toggle) without forcing a full reload.
  const refreshTenant = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/tenants/current`);
      setTenant(res.data);
      return res.data;
    } catch (err) {
      return null;
    }
  }, []);

  const contextValue = useMemo(() => ({ 
    user, 
    tenant,
    loading, 
    login, 
    register, 
    logout, 
    isAuthenticated: !!user,
    getTenantPath,
    tenantSlug: tenant?.slug || localStorage.getItem('tenantSlug') || 'default',
    setUser,
    setTenant,
    refreshTenant,
    ensureBullhornConnection,
  }), [user, tenant, loading, login, register, logout, getTenantPath, refreshTenant, ensureBullhornConnection]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
